/**
 * What a player sees and hears when pieces lock, on either game screen: the
 * clear burst and the shake for a line clear, and the sound for each lock.
 *
 * One function, because the two screens must not drift: a Quad has to look
 * and sound the same solo and in a duel.
 */
import type { InputAction, LockResult } from '@interlock/engine';
import { boardTheme } from '@interlock/themes';

import { audio } from '../audio/engine.js';
import type { EffectsController } from '../render/effects.js';
import { deriveClearEffectProfile } from '../render/theme-effects.js';
import { settings } from '../state/settings.svelte.js';

/** Lines in the largest clear: a Quad. */
const QUAD_LINES = 4;

export interface LockFeedbackInput {
  /** The locks this tick produced on the player's own board. */
  readonly locks: readonly LockResult[];
  /** The actions this tick was stepped with, to tell a hard drop from a lock-delay lock and to hear a hold. */
  readonly actions: readonly InputAction[];
  /** The effects of the board the locks happened on. */
  readonly effects: EffectsController;
  readonly boardWidth: number;
}

/**
 * Triggers the effects and sounds for one tick. Returns how many of the locks
 * cleared lines — the screens count those on `data-clear-fx`, which is what
 * the e2e suite reads instead of canvas pixels.
 */
export function playLockFeedback(input: LockFeedbackInput): number {
  const { locks, actions, effects, boardWidth } = input;
  const hardDropped = actions.includes('hardDrop');
  let clears = 0;

  for (const lock of locks) {
    if (lock.lines > 0) {
      const profile = deriveClearEffectProfile(boardTheme(settings.appearance).tokens);
      effects.triggerClear(lock.clearedRows, lock.kind, boardWidth, profile, {
        quad: lock.lines === QUAD_LINES,
        comboCount: lock.combo,
        backToBack: lock.backToBack,
      });
      effects.triggerShake(Math.min(4, lock.lines * 1.6));
      clears += 1;
      audio.playSfx(lock.lines === QUAD_LINES ? 'quad' : 'clear', { lines: lock.lines });
    } else {
      audio.playSfx(hardDropped ? 'hardDrop' : 'lock');
    }
  }
  if (actions.includes('hold')) audio.playSfx('hold');

  return clears;
}
